import React from 'react';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesAlertTitle,
  AgnesBadge,
  AgnesButton,
  AgnesSelect,
  AgnesSelectionBar,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  type SelectionBarAction,
} from '@agnes/ui';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '@/components/SearchInput';
import { Check, ChevronLeft, ChevronRight, Layers, Pencil } from 'lucide-react';
import { RubricaAnaliseLote } from '@/types/rubricas/analise-lote/RubricaAnaliseLote';
import { CardRubricasAnaliseLote } from '@/pages/folha-pagamento/index/tab-analise-lote/CardRubricasAnaliseLote';
import { ModalRubricasAnaliseLoteConversa } from '@/pages/folha-pagamento/index/tab-analise-lote/ModalRubricasAnaliseLoteConversa';
import { Conversa } from '@/types/conversacao/Conversa';
import ServiceRubricaLote, {
  RubricaLoteContagem,
  RubricaLoteFilters,
} from '@/services/ServiceRubricaLote';
import {
  type AppliedFilters,
  EMPTY_FILTERS,
  requestFilters,
} from '@/components/rubricas/rubricaLoteFilters';
import { ITEM_PAGE_SIZES, PAGE_SIZES } from '@/constants/ConstantRubricaLotePagination';
import TableRubricasAnaliseLoteRegistros from '@/pages/folha-pagamento/index/tab-analise-lote/TableRubricasAnaliseLoteRegistros';
import FilterRubricaAnaliseLoteRegistros from '@/pages/folha-pagamento/index/tab-analise-lote/FilterRubricaAnaliseLoteRegistros';
import { ModalAprovacaoLote } from '@/pages/folha-pagamento/index/tab-analise-lote/ModalAprovacaoLote';
import {
  gerarChaveIdempotencia,
  requisicaoAprovacao,
  requisicaoAprovacaoGrupo,
  type ResumoAprovacao,
  resumoAprovacao,
} from '@/components/rubricas/processamentoLote';
import {
  type EstadoAcaoEmMassa,
  ORIGEM_SELECAO_MULTIPLA,
  ROTA_ACAO_EM_MASSA,
} from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import { Rubrica } from '@/types/rubricas/Rubrica';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';
import { AnaliseStatus } from '@/constants/analise/AnaliseStatus';

const MOCK_DATA_CONVERSACAO: Conversa = {
  id: 1,
  nome: 'Nome do Grupo',
  mensagens: [
    {
      id: 1,
      autor: 'Operação Agnes',
      conteudo: 'Recomendações aplicadas conforme tabela eSocial.',
      enviadoEm: '12/01/2025 14:30:00',
      arquivos: [],
    },
    {
      id: 2,
      autor: 'Maria Andrade · RH',
      conteudo:
        'Podem confirmar a incidência de FGTS nesta natureza? Segue o relatório de conferência.',
      enviadoEm: '12/01/2025 16:02:00',
      arquivos: [{ nome: 'conferencia-fgts.pdf', tamanho: '218 KB', extensao: 'pdf' }],
    },
    {
      id: 1,
      autor: 'Operação Agnes',
      conteudo: 'Confirmado. A incidência segue a tabela vigente - anexamos o parecer técnico.',
      enviadoEm: '13/01/2025 09:14:00',
      arquivos: [{ nome: 'parecer-tecnico.xlsx', tamanho: '46 KB', extensao: 'pdf' }],
    },
  ],
};

const SEM_SELECAO: ReadonlyMap<number, Rubrica> = new Map();

const chaveGrupo = (lote: RubricaAnaliseLote) => JSON.stringify(lote.grupo);

/** O que o modal de aprovação vai aprovar: as rubricas marcadas na barra ou um grupo inteiro. */
type AlvoAprovacao = { tipo: 'selecao' } | { tipo: 'grupo'; lote: RubricaAnaliseLote };

export function TabRubricasAnaliseLote({ refreshToken = 0 }: { refreshToken?: number }) {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<'analisar' | 'analisado'>('analisar');
  const [conversacao, setConversacao] = React.useState<Conversa | undefined>(undefined);

  const [filters, setFilters] = React.useState<AppliedFilters>(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(20);
  const [result, setResult] = React.useState({
    content: [] as RubricaAnaliseLote[],
    totalElements: 0,
    totalPages: 0,
    baseFilters: {} as RubricaLoteFilters,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const [contagem, setContagem] = React.useState<RubricaLoteContagem | null>(null);

  // Seleção por grupo (chave do grupo → rubricas marcadas), somada na barra de ação em massa.
  const [selecao, setSelecao] = React.useState<Record<string, ReadonlyMap<number, Rubrica>>>({});
  const [gruposSelecionando, setGruposSelecionando] = React.useState<Record<string, boolean>>({});
  // Muda a cada recomeço da seleção: itens de grupo que chegarem depois disso são descartados.
  const geracaoSelecao = React.useRef(0);
  const [alvoAprovacao, setAlvoAprovacao] = React.useState<AlvoAprovacao | null>(null);
  const [aprovando, setAprovando] = React.useState(false);
  const chaveAprovacao = React.useRef('');

  const selecionadas = React.useMemo(
    () => Object.values(selecao).flatMap((itens) => [...itens.values()]),
    [selecao],
  );
  const descricoesSelecionadas = Object.values(selecao).filter((itens) => itens.size > 0).length;

  const alterarSelecaoGrupo = async (lote: RubricaAnaliseLote, selecionado: boolean) => {
    const chave = chaveGrupo(lote);
    if (!selecionado) {
      setSelecao((current) => {
        const next = { ...current };
        delete next[chave];
        return next;
      });
      return;
    }

    const geracao = geracaoSelecao.current;
    setGruposSelecionando((current) => ({ ...current, [chave]: true }));
    const size = Math.max(...ITEM_PAGE_SIZES);
    const request = {
      grupo: lote.grupo,
      baseFilters: result.baseFilters,
      itemFilters: {},
      size,
    };

    try {
      const { data: primeiraPagina } = await ServiceRubricaLote.consultarItens({
        ...request,
        page: 0,
      });
      const paginasRestantes = await Promise.all(
        Array.from({ length: Math.max(0, primeiraPagina.totalPages - 1) }, (_, index) =>
          ServiceRubricaLote.consultarItens({ ...request, page: index + 1 }).then(
            ({ data }) => data.content,
          ),
        ),
      );
      const itens = [primeiraPagina.content, ...paginasRestantes].flat();
      if (geracao !== geracaoSelecao.current) return;

      setSelecao((current) => {
        const itensSelecionados = new Map(current[chave] ?? []);
        itens.forEach((item) => itensSelecionados.set(item.idAnaliseRubrica, item));
        return { ...current, [chave]: itensSelecionados };
      });
    } catch (err) {
      if (geracao !== geracaoSelecao.current) return;
      notify.error(
        mensagemErro(err, `Não foi possível carregar as rubricas do grupo "${lote.descricao}".`),
      );
    } finally {
      setGruposSelecionando((current) => ({ ...current, [chave]: false }));
    }
  };

  // Uma nova consulta de grupos pode esconder itens marcados; a seleção recomeça.
  React.useEffect(() => {
    geracaoSelecao.current += 1;
    setSelecao({});
  }, [filters, page, refreshToken, reload, search, size, tab]);

  const editarEmMassa = () => {
    const state: EstadoAcaoEmMassa = {
      rubricas: selecionadas,
      origem:
        descricoesSelecionadas === 1
          ? (selecionadas[0]?.descricao ?? ORIGEM_SELECAO_MULTIPLA)
          : ORIGEM_SELECAO_MULTIPLA,
    };
    navigate(ROTA_ACAO_EM_MASSA, { state });
  };

  const abrirAprovacao = (alvo: AlvoAprovacao) => {
    // Mesma chave enquanto o modal estiver aberto: repetir após falha de rede não duplica o lote.
    chaveAprovacao.current = gerarChaveIdempotencia();
    setAlvoAprovacao(alvo);
  };

  const resumo: ResumoAprovacao =
    alvoAprovacao?.tipo === 'grupo'
      ? {
          total: alvoAprovacao.lote.totais.paraAnalisar,
          descricoes: [alvoAprovacao.lote.descricao],
          naturezas: alvoAprovacao.lote.naturezaCodigos,
        }
      : resumoAprovacao(selecionadas);

  const podeEditar = usePodeEditar(MENU_PERMISSIONS.FOLHA_DE_PAGAMENTO_EDITAR);

  // Aprovação só existe para rubricas ainda não analisadas; editar e aprovar exigem escrita.
  const podeAprovar = tab === 'analisar';
  const quantidadeSelecionada = selecionadas.length.toLocaleString('pt-BR');
  const acoesSelecao: SelectionBarAction[] = podeEditar
    ? [
        {
          id: 'editar',
          label: podeAprovar ? 'Editar em massa' : `Editar ${quantidadeSelecionada} em massa`,
          variant: podeAprovar ? 'outline' : 'filled',
          icon: <Pencil className="h-4 w-4" />,
          disabled: aprovando,
          onClick: editarEmMassa,
        },
        ...(podeAprovar
          ? [
              {
                id: 'aprovar',
                label: `Aprovar ${quantidadeSelecionada} em lote`,
                icon: <Check className="h-4 w-4" />,
                loading: aprovando,
                onClick: () => abrirAprovacao({ tipo: 'selecao' }),
              },
            ]
          : []),
      ]
    : [];

  const aprovar = () => {
    if (!alvoAprovacao) return;
    const grupo = alvoAprovacao.tipo === 'grupo' ? alvoAprovacao.lote : null;
    setAprovando(true);
    ServiceRubricaLote.criarProcessamento(
      grupo
        ? requisicaoAprovacaoGrupo(grupo.grupo, result.baseFilters)
        : requisicaoAprovacao(selecionadas),
      chaveAprovacao.current,
    )
      .then(() => {
        notify.success(
          grupo
            ? `Aprovação do grupo "${grupo.descricao}" enviada para processamento.`
            : `Aprovação de ${selecionadas.length.toLocaleString('pt-BR')} ${selecionadas.length === 1 ? 'rubrica' : 'rubricas'} enviada para processamento.`,
        );
        setAlvoAprovacao(null);
        setReload((current) => current + 1);
      })
      .catch((err) =>
        notify.error(mensagemErro(err, 'Não foi possível aprovar as rubricas em lote.')),
      )
      .finally(() => setAprovando(false));
  };

  // Totais das abas: não dependem da aba nem da página, só dos filtros. Falha só esconde os números.
  React.useEffect(() => {
    let ignore = false;
    setContagem(null);
    ServiceRubricaLote.consultarContagem(requestFilters(filters, search))
      .then(({ data }) => {
        if (!ignore) setContagem(data);
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, [filters, refreshToken, reload, search]);

  React.useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);

    const statusAnalise: AnaliseStatus[] =
      tab === 'analisar' ? ['EM_ANALISE', 'PENDENTE_DE_ESCLARECIMENTO'] : ['ANALISADO'];
    const baseFilters = { ...requestFilters(filters, search), statusAnalise };

    ServiceRubricaLote.consultarGrupos({
      groupBy: 'DESCRICAO',
      filters: baseFilters,
      page,
      size,
    })
      .then(({ data }) => {
        if (!ignore) {
          setResult({ ...data, baseFilters });
          if (page > 0 && page >= data.totalPages) setPage(Math.max(0, data.totalPages - 1));
        }
      })
      .catch(() => {
        if (!ignore) setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [filters, page, refreshToken, reload, search, size, tab]);

  return (
    <>
      <AgnesAlert
        icon={<Layers className="h-4 w-4 text-agnes-primary" />}
        className={'rounded-xl mb-4 bg-agnes-primary/5'}
      >
        <AgnesAlertTitle>Rubricas agrupadas por descrição idêntica</AgnesAlertTitle>

        <AgnesAlertContent>
          <p>
            Uma mesma descrição pode reunir naturezas distintas. Aprove o grupo inteiro, ou expanda
            e selecione uma ou mais rubricas. Use os filtros dentro do grupo para lidar com alto
            volume.
          </p>
        </AgnesAlertContent>
      </AgnesAlert>

      <div className="rounded-xl border border-agnes-border bg-agnes-bg-surface p-4">
        <AgnesTabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as 'analisar' | 'analisado');
            setPage(0);
          }}
        >
          <AgnesTabsList>
            <AgnesTabsTrigger value="analisar">
              Para analisar{' '}
              <ContadorAba
                dataQa="tab-analise-lote-contagem-analisar"
                total={contagem?.paraAnalisar}
              />
            </AgnesTabsTrigger>
            <AgnesTabsTrigger value="analisado">
              Analisado{' '}
              <ContadorAba
                dataQa="tab-analise-lote-contagem-analisado"
                total={contagem?.analisado}
              />
            </AgnesTabsTrigger>
          </AgnesTabsList>

          <AgnesTabsContent value={tab}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[320px] flex-1">
                <SearchInput
                  value={searchInput}
                  onValueChange={setSearchInput}
                  onSearch={(value) => {
                    setSearch(value.trim());
                    setPage(0);
                  }}
                  placeholder="Pesquisar por descrição, código ou natureza da rubrica"
                />
              </div>

              <FilterRubricaAnaliseLoteRegistros
                dataQa="rubricas-lote-filtro-global"
                filters={filters}
                refreshToken={refreshToken}
                onApply={(value) => {
                  setFilters(value);
                  setPage(0);
                }}
              />
            </div>

            {error && (
              <AgnesAlert dataQa="listagem-lote-erro" preset="error" className="mt-4">
                <AgnesAlertTitle>Erro</AgnesAlertTitle>
                <AgnesAlertContent>
                  Não foi possível carregar os grupos de rubricas. Tente novamente.
                </AgnesAlertContent>
              </AgnesAlert>
            )}

            {loading ? (
              <div
                data-qa="listagem-lote-carregando"
                className="mt-4 rounded-xl border border-agnes-border p-8 text-center text-sm text-agnes-text-muted"
              >
                Carregando grupos de rubricas...
              </div>
            ) : !error && result.content.length === 0 ? (
              <div
                data-qa="listagem-lote-vazio"
                className="mt-4 rounded-xl border border-dashed border-agnes-border p-8 text-center text-sm text-agnes-text-muted"
              >
                Nenhum grupo de rubricas encontrado para os filtros informados.
              </div>
            ) : (
              !error && (
                <div data-qa={`listagem-lote-registros`} className="mt-4 flex flex-col gap-3">
                  {result.content.map((lote) => (
                    <CardRubricasAnaliseLote
                      key={chaveGrupo(lote)}
                      dataQa={`listagem-lote-registro-${encodeURIComponent(JSON.stringify(lote.grupo))}`}
                      lote={lote}
                      analisado={tab === 'analisado'}
                      selecionado={
                        (selecao[chaveGrupo(lote)]?.size ?? 0) >= lote.totais.rubricas &&
                        lote.totais.rubricas > 0
                      }
                      selecionando={gruposSelecionando[chaveGrupo(lote)] ?? false}
                      onSelecaoGrupoChange={(selecionado) => alterarSelecaoGrupo(lote, selecionado)}
                      onAprovarGrupo={
                        podeEditar ? () => abrirAprovacao({ tipo: 'grupo', lote }) : undefined
                      }
                      onClickConversacao={() => setConversacao(MOCK_DATA_CONVERSACAO)}
                    >
                      <TableRubricasAnaliseLoteRegistros
                        dataQa={`listagem-lote-registro-${encodeURIComponent(JSON.stringify(lote.grupo))}-itens`}
                        lote={lote}
                        baseFilters={result.baseFilters}
                        selected={selecao[chaveGrupo(lote)] ?? SEM_SELECAO}
                        onSelectedChange={(next) =>
                          setSelecao((current) => ({ ...current, [chaveGrupo(lote)]: next }))
                        }
                      />
                    </CardRubricasAnaliseLote>
                  ))}
                </div>
              )
            )}

            {!error && !loading && result.totalElements > 0 && (
              <div
                data-qa={`tab-analise-lote-paginacao`}
                className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-agnes-border pt-4"
              >
                <span
                  data-qa={`tab-analise-lote-paginacao-total`}
                  className="text-sm text-agnes-text-muted"
                >
                  {result.totalElements.toLocaleString('pt-BR')}{' '}
                  {result.totalElements === 1 ? 'grupo' : 'grupos'}
                </span>

                <div
                  data-qa={`tab-analise-lote-paginacao-controles`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <AgnesSelect
                    value={String(size)}
                    onValueChange={(value) => {
                      setSize(Number(value));
                      setPage(0);
                    }}
                  >
                    <AgnesSelectTrigger
                      data-qa={`tab-analise-lote-paginacao-tamanho-trigger`}
                      className="w-24"
                      aria-label="Itens por página"
                    >
                      <AgnesSelectValue data-qa={`tab-analise-lote-paginacao-tamanho-valor`} />
                    </AgnesSelectTrigger>

                    <AgnesSelectContent data-qa={`tab-analise-lote-paginacao-tamanho-popover`}>
                      {PAGE_SIZES.map((option) => (
                        <AgnesSelectItem
                          data-qa={`tab-analise-lote-paginacao-tamanho-opcao-${option}`}
                          key={option}
                          value={String(option)}
                        >
                          {option}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>

                  <span
                    data-qa={`tab-analise-lote-paginacao-pagina`}
                    className="min-w-24 text-center text-sm text-agnes-text-muted"
                  >
                    Página {page + 1} de {result.totalPages}
                  </span>

                  <AgnesButton
                    data-qa={`tab-analise-lote-paginacao-anterior`}
                    variant="outlined"
                    aria-label="Página anterior"
                    disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    <ChevronLeft
                      data-qa={`tab-analise-lote-paginacao-anterior-icone`}
                      className="h-4 w-4"
                    />
                  </AgnesButton>

                  <AgnesButton
                    data-qa={`tab-analise-lote-paginacao-proxima`}
                    variant="outlined"
                    aria-label="Próxima página"
                    disabled={page + 1 >= result.totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    <ChevronRight
                      data-qa={`tab-analise-lote-paginacao-proxima-icone`}
                      className="h-4 w-4"
                    />
                  </AgnesButton>
                </div>
              </div>
            )}
          </AgnesTabsContent>
        </AgnesTabs>

        <AgnesSelectionBar
          dataQa="rubricas-selecao-barra"
          count={selecionadas.length}
          singularLabel="rubrica selecionada"
          pluralLabel="rubricas selecionadas"
          subtitle={descricoesSelecionadas > 1 ? 'em múltiplas descrições' : 'em 1 descrição'}
          clearDisabled={aprovando}
          onClear={() => {
            geracaoSelecao.current += 1;
            setSelecao({});
          }}
          actions={acoesSelecao}
        />
      </div>

      <ModalAprovacaoLote
        open={!!alvoAprovacao}
        resumo={resumo}
        aprovando={aprovando}
        onClose={() => setAlvoAprovacao(null)}
        onConfirmar={aprovar}
      />

      <ModalRubricasAnaliseLoteConversa
        conversacao={conversacao}
        open={!!conversacao}
        onClose={() => setConversacao(undefined)}
      />
    </>
  );
}

function ContadorAba({ total, dataQa }: { total?: number; dataQa: string }) {
  if (total === undefined) return null;
  return (
    <AgnesBadge dataQa={dataQa} size="sm" variant="secondary" className="ml-1 tabular-nums">
      {total.toLocaleString('pt-BR')}
    </AgnesBadge>
  );
}
